import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUserPlus, FaShoppingBag, FaFileSignature, FaCookie, FaBirthdayCake,
  FaClipboardList, FaCalendarAlt, FaIndustry, FaCarrot, FaExchangeAlt,
  FaBox, FaCalculator, FaDollarSign, FaTags, FaWarehouse,
} from 'react-icons/fa';
import PageLayout from './PageLayout';
import { theme } from '../theme';

// ──────────────────────────────────────────────────────────────────
// Configuração das categorias (cor + ícones + atalhos)
// ──────────────────────────────────────────────────────────────────

const CATEGORIAS = [
  {
    id: 'vendas',
    titulo: 'Vendas',
    descricao: 'Pedidos, orçamentos e clientes',
    accent: theme.colors.primary,        // #C4786E rosa Metria
    surface: theme.colors.primaryLight,  // #F5E6E3
    softAccent: '#E8B4A0',
    cardMinWidth: 240,
    atalhos: [
      { icon: <FaShoppingBag />,    label: 'Novo pedido',         desc: 'Registre uma nova encomenda',     to: '/pedidos?novo=1' },
      { icon: <FaUserPlus />,       label: 'Cadastrar cliente',   desc: 'Adicione um novo cliente',        to: '/clientes?novo=1' },
      { icon: <FaFileSignature />,  label: 'Novo orçamento',      desc: 'Monte um orçamento sem compromisso', to: '/orcamentos?novo=1' },
      { icon: <FaDollarSign />,     label: 'Lançar transação',    desc: 'Receita ou despesa',              to: '/financas?novo=1' },
    ],
  },
  {
    id: 'producao',
    titulo: 'Produção',
    descricao: 'Receitas, bolos e guias do dia',
    accent: '#E91E63',                   // rosa vivo (raspberry)
    surface: '#FCE4EC',
    softAccent: '#F48FB1',
    cardMinWidth: 240,
    atalhos: [
      { icon: <FaClipboardList />,  label: 'Preparações Base',    desc: 'Massas, recheios, caldas e coberturas', to: '/fichas-tecnicas/base' },
      { icon: <FaTags />,           label: 'Fichas de Produto',   desc: 'Precificação e CMV de produtos finais', to: '/fichas-tecnicas/produto' },
      { icon: <FaCookie />,         label: 'Cadastrar recheio',   desc: 'Defina sabores de recheio',       to: '/guia/recheios' },
      { icon: <FaBirthdayCake />,   label: 'Tamanho de bolo',     desc: 'Configure massas e medidas',      to: '/guia/tamanhos' },
      { icon: <FaCalendarAlt />,    label: 'Montagem diária',     desc: 'Fichas do dia',                   to: '/guia/montagem' },
      { icon: <FaIndustry />,       label: 'Produção de bolos',   desc: 'Calcule o que preparar',          to: '/guia/producao' },
      { icon: <FaCalculator />,     label: 'Calculadora',         desc: 'Escale uma receita por gramas',   to: '/guia/calculadora' },
    ],
  },
  {
    id: 'estoque',
    titulo: 'Estoque',
    descricao: 'Ingredientes, embalagens e movimentações',
    accent: '#D9A21A',                   // amber/gold visível
    surface: '#FEF6E1',
    softAccent: '#F0C75E',
    cardMinWidth: 240,
    atalhos: [
      { icon: <FaCarrot />,                label: 'Novo ingrediente',  desc: 'Cadastre um insumo',          to: '/estoque?novo=1' },
      { icon: <FaExchangeAlt />,           label: 'Entrada/Saída',     desc: 'Lançar movimentação',         to: '/estoque?movimentar=1' },
      { icon: <FaBox />,                   label: 'Nova embalagem',    desc: 'Caixas, cakeboards, fitas',   to: '/embalagens?novo=1' },
      { icon: <FaWarehouse />,             label: 'Inventário',        desc: 'Visão geral do estoque',      to: '/inventario' },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────
// Componente
// ──────────────────────────────────────────────────────────────────

export default function Atalhos() {
  const navigate = useNavigate();

  return (
    <PageLayout title="Atalhos" subtitle="As ações mais usadas do Metria, organizadas por área">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {CATEGORIAS.map(cat => (
          <CategoriaSection key={cat.id} categoria={cat} onAtalhoClick={navigate} />
        ))}
      </div>
    </PageLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
// Seção de categoria
// ──────────────────────────────────────────────────────────────────

function CategoriaSection({ categoria, onAtalhoClick }) {
  return (
    <section>
      {/* Cabeçalho da categoria */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 6, height: 22, borderRadius: 3,
            background: categoria.accent,
          }}
        />
        <div>
          <h2 style={{
            margin: 0, fontSize: 18, fontWeight: 700,
            color: theme.colors.text, letterSpacing: '-0.01em',
          }}>
            {categoria.titulo}
          </h2>
          <p style={{
            margin: '2px 0 0', fontSize: 13,
            color: theme.colors.textMuted,
          }}>
            {categoria.descricao}
          </p>
        </div>
      </div>

      {/* Grade de atalhos */}
      <div
        className="atalhos-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${categoria.cardMinWidth || 200}px, 1fr))`,
          gap: 12,
        }}
      >
        {categoria.atalhos.map((a, i) => (
          <AtalhoCard
            key={i}
            atalho={a}
            accent={categoria.accent}
            surface={categoria.surface}
            softAccent={categoria.softAccent}
            onClick={() => onAtalhoClick(a.to)}
          />
        ))}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Card de atalho
// ──────────────────────────────────────────────────────────────────

function AtalhoCard({ atalho, accent, surface, softAccent, onClick }) {
  const [hover, setHover] = React.useState(false);
  const [press, setPress] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      aria-label={`${atalho.label}. ${atalho.desc}`}
      className="atalho-card"
      style={{
        // Layout
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        textAlign: 'left',
        padding: '16px 16px 18px',
        minHeight: 132,
        borderRadius: 16,
        cursor: 'pointer',
        userSelect: 'none',
        // Visual base
        background: '#fff',
        border: `1px solid ${hover ? accent : theme.colors.borderLight}`,
        boxShadow: hover
          ? `0 8px 24px ${accent}22, 0 1px 2px rgba(0,0,0,0.04)`
          : '0 1px 2px rgba(0,0,0,0.04)',
        // Movimento (≤200ms, transform-only, respeita reduced motion via CSS prefers no motion fallback abaixo)
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        transform: press ? 'translateY(0) scale(0.985)' : (hover ? 'translateY(-2px)' : 'translateY(0)'),
        outline: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
      onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 3px ${accent}33`; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = hover
        ? `0 8px 24px ${accent}22, 0 1px 2px rgba(0,0,0,0.04)`
        : '0 1px 2px rgba(0,0,0,0.04)'; }}
    >
      {/* Faixa decorativa de cor (gradiente sutil no canto) */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -28, right: -28,
          width: 90, height: 90,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${softAccent}55 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Ícone em "selo" */}
      <span
        aria-hidden="true"
        className="atalho-icon"
        style={{
          width: 44, height: 44, borderRadius: 12,
          background: surface,
          color: accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
          marginBottom: 12,
          transition: 'transform 180ms ease',
          transform: hover ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {atalho.icon}
      </span>

      {/* Título */}
      <span
        className="atalho-label"
        style={{
          fontSize: 15, fontWeight: 600,
          color: theme.colors.text,
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
          marginBottom: 4,
        }}
      >
        {atalho.label}
      </span>

      {/* Descrição */}
      <span
        className="atalho-desc"
        style={{
          fontSize: 12, fontWeight: 400,
          color: theme.colors.textMuted,
          lineHeight: 1.4,
        }}
      >
        {atalho.desc}
      </span>
    </button>
  );
}
